import React, { useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Building2, User } from 'lucide-react';
import { cn } from '../../lib/utils';

const ROLE_COLOURS = {
  customer: 'border-blue-400 bg-blue-50 dark:bg-blue-950',
  supplier: 'border-green-400 bg-green-50 dark:bg-green-950',
  subcontractor: 'border-orange-400 bg-orange-50 dark:bg-orange-950'
};

function CustomerNode({ data }) {
  return (
    <div
      className={cn(
        'rounded-lg border-2 px-4 py-2 shadow-sm bg-background min-w-[140px] text-center',
        data.isCurrent ? 'border-primary ring-2 ring-primary/30' : 'border-muted-foreground/30'
      )}
    >
      <User className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
      <div className="text-sm font-medium truncate">{data.label}</div>
    </div>
  );
}

function OrganisationNode({ data }) {
  const roles = data.roles ? data.roles.split(',').map((r) => r.trim()) : [];
  const firstRole = roles[0] || 'customer';
  const colour = ROLE_COLOURS[firstRole] || 'border-muted-foreground/30 bg-background';

  return (
    <div
      className={cn(
        'rounded-lg border-2 px-4 py-2 shadow-sm min-w-[140px] text-center',
        colour,
        data.isCurrent && 'ring-2 ring-primary/30'
      )}
    >
      <Building2 className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
      <div className="text-sm font-medium truncate">{data.label}</div>
      {roles.length > 0 && (
        <div className="flex justify-center gap-1 mt-1">
          {roles.map((r) => (
            <span key={r} className="text-[10px] capitalize text-muted-foreground">{r}</span>
          ))}
        </div>
      )}
    </div>
  );
}

const nodeTypes = {
  customer: CustomerNode,
  organisation: OrganisationNode
};

const defaultEdgeOptions = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
  style: { strokeWidth: 1.5 }
};

/**
 * RelationshipGraph - Interactive @xyflow/react graph visualisation for entity relationships
 *
 * @param {Object} props
 * @param {{ nodes: Array, edges: Array }} props.graphData - Pre-fetched graph data from getRelationshipGraphData
 * @param {Function} [props.onNodeClick] - Callback when a node is clicked (receives node)
 * @param {string} [props.className] - Additional CSS classes for the container
 */
export default function RelationshipGraph({ graphData, onNodeClick, className }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    if (!graphData) return;

    const flowNodes = graphData.nodes.map((n) => ({
      ...n,
      type: n.type,
      draggable: true
    }));

    const flowEdges = graphData.edges.map((e) => ({
      ...e,
      ...defaultEdgeOptions,
      label: e.label,
      animated: e.animated || false,
      labelStyle: { fontSize: 11, fill: 'var(--muted-foreground)' }
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [graphData, setNodes, setEdges]);

  const handleNodeClick = useCallback(
    (_event, node) => {
      onNodeClick?.(node);
    },
    [onNodeClick]
  );

  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div className={cn('flex items-center justify-center text-sm text-muted-foreground py-8', className)}>
        No relationships to display
      </div>
    );
  }

  return (
    <div className={cn('w-full h-[400px] rounded-md border bg-muted/20', className)}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.3}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
